import React, { useRef, useLayoutEffect } from 'react';
import {
    Box,
    VStack,
    Text,
    Divider,
    Image,
    Flex,
    Icon,
    Badge,
    useColorModeValue,
    Drawer,
    DrawerContent,
    DrawerOverlay,
    DrawerCloseButton,
    DrawerBody,
} from '@chakra-ui/react';
import { NavLink as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    MdDashboard,
    MdNotifications,
    MdBugReport,
    MdPlaylistAddCheck,
    MdBarChart,
    MdStorefront
} from 'react-icons/md';

/**
 * Support Desk User Sidebar Navigation Component
 */
function UserSidebar({ isOpen, onClose, variant, userProfile, appConfig, issuesStats, ...rest }) {
    const location = useLocation();
    const navigate = useNavigate();
    const scrollContainerRef = useRef(null);
    const scrollPosition = useRef(0);
    const observerRef = useRef(null);
    
    // Aggressive scroll position lock
    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        // Create a MutationObserver to watch for any DOM changes that might affect scroll
        observerRef.current = new MutationObserver(() => {
            if (container.scrollTop !== scrollPosition.current) {
                container.scrollTop = scrollPosition.current;
            }
        });

        // Start observing
        observerRef.current.observe(container, {
            childList: true,
            subtree: true,
            attributes: true
        });

        // Also use ResizeObserver to handle any layout changes
        const resizeObserver = new ResizeObserver(() => {
            if (container.scrollTop !== scrollPosition.current) {
                container.scrollTop = scrollPosition.current;
            }
        });

        resizeObserver.observe(container);

        return () => {
            observerRef.current?.disconnect();
            resizeObserver.disconnect();
        };
    }, []);

    // Save scroll position on every scroll
    const handleScroll = (e) => {
        scrollPosition.current = e.target.scrollTop;
    };

    // Restore scroll position after route change
    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.scrollTop = scrollPosition.current;
            
            // Force it multiple times to be absolutely sure
            requestAnimationFrame(() => {
                container.scrollTop = scrollPosition.current;
                requestAnimationFrame(() => {
                    container.scrollTop = scrollPosition.current;
                });
            });
        }
    }, [location.pathname]);
    
    // Theme-aware colors
    const sidebarBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.700', 'gray.200');
    const headingColor = useColorModeValue('gray.900', 'white');
    const activeColor = useColorModeValue('brand.500', 'brand.300');
    const hoverBg = useColorModeValue('gray.50', 'gray.700');
    
    // Scrollbar colors
    const scrollbarTrack = useColorModeValue('#f1f1f1', '#2d3748');
    const scrollbarThumb = useColorModeValue('#888', '#4a5568');
    const scrollbarThumbHover = useColorModeValue('#555', '#718096');

    /**
     * Navigation menu items configuration for Support Desk User
     * Issues menu item with badge support
     */
    const navigationItems = [
        {
            name: 'Analytics',
            icon: MdDashboard,
            path: '/user-analytics',
            description: 'Client monitoring and analytics'
        },
        {
            name: 'Issues',
            icon: MdBugReport,
            path: '/issues',
            description: 'Report triggered issue management',
            badge: 'issuesStats' // Special flag to show issues badge
        },
        {
            name: 'Notifications',
            icon: MdNotifications,
            path: '/user-notifications',
            description: 'System alerts and messages'
        },
         { name: 'Checklist Results', icon: MdPlaylistAddCheck, path: '/checklist-results', description: 'Client CheckList Results' },
         { name: 'Dynamic Report Results', icon: MdBarChart, path: '/dynamic-report-results', description: 'Client dynamic report execution results' },
         { name: 'Market Data ETL Monitor', icon: MdStorefront,       path: '/market-data-results',     description: 'Market data ETL run status per client' },
    ];

    /**
     * Helper function to get badge content for navigation items
     */
    const getBadgeContent = (item) => {
        if (item.badge === 'issuesStats' && issuesStats) {
            // Show total open issues count for Issues menu item
            return issuesStats.openIssues > 0 ? issuesStats.openIssues : null;
        }
        return null;
    };
    
    /**
     * Helper function to get badge color scheme based on issue count
     */
    const getBadgeColorScheme = (issueCount) => {
        if (issueCount > 50) return 'red';
        if (issueCount > 20) return 'orange';  
        if (issueCount > 10) return 'yellow';
        if (issueCount > 0) return 'blue';
        return 'green';
    };

    /**
     * Navigation Item Component with badge support
     * Renders individual navigation links with active states and hover effects
     */
    const NavItem = ({ item, onClick }) => {
        const isActive = location.pathname === item.path;
        const badgeContent = getBadgeContent(item);
        const badgeColorScheme = getBadgeColorScheme(issuesStats?.openIssues || 0);
        
        const handleClick = (e) => {
            if (item.disabled) {
                e.preventDefault();
                return;
            }

            // Save current scroll position
            if (scrollContainerRef.current) {
                scrollPosition.current = scrollContainerRef.current.scrollTop;
            }
            
            // Call original onClick if provided (for mobile drawer close)
            if (onClick) {
                onClick(e);
            }
            
            // Navigate to new route
            if (item.path !== location.pathname) {
                navigate(item.path);
            }
        };
        
        return (
            <Box
                as="button"
                onClick={handleClick}
                w="full"
                textAlign="left"
                bg="transparent"
                border="none"
                p={0}
                cursor={item.disabled ? 'not-allowed' : 'pointer'}
                opacity={item.disabled ? 0.5 : 1}
                _focus={{ outline: 'none' }}
            >
                <Flex
                    align="center"
                    p={3}
                    mx={2}
                    borderRadius="lg"
                    role="group"
                    bg={isActive ? activeColor : 'transparent'}
                    color={isActive ? 'white' : textColor}
                    _hover={!item.disabled ? {
                        bg: isActive ? activeColor : hoverBg,
                        color: isActive ? 'white' : headingColor
                    } : {}}
                    transition="all 0.2s"
                    position="relative"
                >
                    <Icon
                        mr={4}
                        fontSize="20"
                        as={item.icon}
                        opacity={isActive ? 1 : 0.7}
                    />
                    <Box flex="1">
                        <Text fontWeight={isActive ? '600' : '500'} fontSize="sm">
                            {item.name}
                        </Text>
                        {item.description && (
                            <Text 
                                fontSize="xs" 
                                opacity={0.7}
                                display={{ base: 'none', xl: 'block' }}
                            >
                                {item.description}
                            </Text>
                        )}
                    </Box>
                    
                    {/* Badge for menu items */}
                    {badgeContent && (
                        <Badge
                            colorScheme={badgeColorScheme}
                            variant="solid"
                            borderRadius="full"
                            fontSize="xs"
                            minW="20px"
                            h="20px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            ml={2}
                        >
                            {badgeContent}
                        </Badge>
                    )}
                </Flex>
            </Box>
        );
    };

    /**
     * Section Header Component
     * Renders section titles with consistent styling
     */
    const SectionHeader = ({ title }) => (
        <Text
            fontSize="xs"
            fontWeight="bold"
            color={textColor}
            textTransform="uppercase"
            letterSpacing="wide"
            px={4}
            py={2}
            opacity={0.7}
        >
            {title}
        </Text>
    );

    /**
     * Sidebar Content Component
     * Contains the actual sidebar content for Support Desk User
     */
    const SidebarContent = ({ onClose, isDrawer = false }) => (
        <Box
            bg={sidebarBg}
            borderRight={!isDrawer ? "1px" : "none"}
            borderRightColor={borderColor}
            w="250px"
            h="100vh"
            maxH="100vh"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            {...rest}
        >
            {/* Logo/Header Section - Fixed height 64px to match navbar */}
            <Flex
                align="center"
                p={4}
                h="64px"
                borderBottom="1px"
                borderBottomColor={borderColor}
                flexShrink={0}
            >
                <Image  src="prismicon96.png"  alt="Prism Monitor"  height="40px"  width="auto" paddingRight={2} />
                <Box>
                    <Text 
                        fontSize="lg" 
                        fontWeight="bold" 
                        color={headingColor}
                        lineHeight="shorter"
                    >
                        Prism Monitor
                    </Text>
                    <Text 
                        fontSize="xs" 
                        color={textColor}
                        opacity={0.7}
                    >
                        Support Desk Interface
                    </Text>
                </Box>
            </Flex>

            {/* Scrollable Navigation Section */}
            <Box 
                ref={scrollContainerRef}
                flex="1" 
                overflowY="scroll"
                overflowX="hidden"
                h="calc(100vh - 64px - 80px)"
                className="user-sidebar-scroll"
                onScroll={handleScroll}
                css={{
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        display: 'block',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: scrollbarTrack,
                        borderRadius: '4px',
                        margin: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: scrollbarThumb,
                        borderRadius: '4px',
                        border: `1px solid ${scrollbarTrack}`,
                        '&:hover': {
                            background: scrollbarThumbHover
                        }
                    },
                    '&::-webkit-scrollbar-corner': {
                        background: 'transparent',
                    },
                    // Firefox scrollbar styling
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
                    // Prevent browser scroll restoration
                    scrollBehavior: 'auto',
                }}
            >
                <VStack spacing={0} align="stretch" pt={4} pb={4}>
                    {/* Support Desk Section */}
                    <Box mb={4}>
                        <SectionHeader title="Support Desk" />
                        <VStack spacing={1} align="stretch" mt={2}>
                            {navigationItems.map((item) => (
                                <NavItem 
                                    key={item.path} 
                                    item={item} 
                                    onClick={onClose}
                                />
                            ))}
                        </VStack>
                    </Box>

                    {/* User Info Section */}
                    <Box>
                        <SectionHeader title="User Information" />
                        <Box px={4} py={2}>
                            <Text fontSize="xs" color={textColor} opacity={0.7}>
                                Role: Support Desk User
                            </Text>
                            {userProfile?.clientGroup && (
                                <Text fontSize="xs" color={textColor} opacity={0.7} mt={1}>
                                    Access: {userProfile.clientGroup === 'all' ? 'All Clients' : 'Limited Clients'}
                                </Text>
                            )}
                        </Box>
                    </Box>
                </VStack>
            </Box>

            {/* Footer Section - Fixed at bottom */}
            <Box 
                p={4} 
                borderTop="1px" 
                borderTopColor={borderColor}
                flexShrink={0}
                minH="80px"
            >
                <Text 
                    fontSize="xs" 
                    color={textColor}
                    opacity={0.6}
                    textAlign="center"
                >
                    {appConfig?.app?.name || 'Prism Monitor'} v{appConfig?.app?.version || '1.0.0'}
                </Text>
                {userProfile?.email && (
                    <Text 
                        fontSize="xs" 
                        color={textColor}
                        opacity={0.6}
                        textAlign="center"
                        mt={1}
                        noOfLines={1}
                    >
                        {userProfile.email}
                    </Text>
                )}
                <Text 
                    fontSize="xs" 
                    color={activeColor}
                    textAlign="center"
                    mt={1}
                    fontWeight="medium"
                >
                    Support Desk Portal
                </Text>
                
                {/* Issues summary in footer for quick reference */}
                {issuesStats && issuesStats.openIssues > 0 && (
                    <Flex justify="center" mt={2}>
                        <Badge
                            colorScheme={getBadgeColorScheme(issuesStats.openIssues)}
                            variant="subtle"
                            borderRadius="full"
                            fontSize="xs"
                            px={2}
                        >
                            {issuesStats.openIssues} open issue{issuesStats.openIssues === 1 ? '' : 's'}
                        </Badge>
                    </Flex>
                )}
            </Box>
        </Box>
    );

    // Render mobile drawer
    if (variant === 'drawer') {
        return (
            <Drawer
                autoFocus={false}
                isOpen={isOpen}
                placement="left"
                onClose={onClose}
                returnFocusOnClose={false}
                onOverlayClick={onClose}
                size="xs"
            >
                <DrawerOverlay bg="blackAlpha.300" />
                <DrawerContent maxW="250px">
                    <DrawerCloseButton 
                        position="absolute"
                        right={4}
                        top={4}
                        zIndex={10}
                        size="md"
                    />
                    <DrawerBody p={0}>
                        <SidebarContent onClose={onClose} isDrawer={true} />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        );
    }

    // Static sidebar for desktop
    return <SidebarContent onClose={onClose} isDrawer={false} />;
}

export default UserSidebar;